import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Webinar, WebinarTrack, webinarSortFn } from 'models/webinars';
import styled from 'styled-components';
import LiveWebinarItem from 'pages/webinar/live/item/LiveWebinarItem';
import MultiTracksHorizontalScrollbarHider from 'pages/webinar/live/item/MultiTracksHorizontalScrollbarHider';
import { BREAKPOINT } from 'pages/webinar/constants';
import { LiveWebinarListOptions } from 'pages/webinar/live/LiveWebinarListOptions';
import { v4 as uuidv4 } from 'uuid';
import { selectLocale } from 'models/localization';
import { useSelector } from 'react-redux';
import { useMediaQuery } from '@material-ui/core';

export interface LiveWebinarTracksGroup {
  trackIdWebinarMap: { [trackId: number]: Webinar };
  tracks: WebinarTrack[];
}

const RootContainer = styled.div<{showTimeLine: boolean}>`
  position: relative;

  @media (min-width: ${BREAKPOINT}px) {
    margin-left: -75px;
    margin-right: -75px;

    &::before {
      z-index: 2;
      content: "";
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      background: rgba(255, 255, 255, 0.8);
      width: ${(props) => (props.showTimeLine ? 148 : 75)}px;
    }

    &::after {
      z-index: 2;
      content: "";
      height: 100%;
      position: absolute;
      right: 0;
      top: 0;
      background: rgba(255, 255, 255, 0.8);
      width: 75px;
    }
  }
`;

interface GroupedWebinarTracksContainerProps {
  keepTimeLineSpace: boolean;
  numWebinars: number;
  isOverflow: boolean;
}

const GroupedWebinarTracksContainer = styled.div<GroupedWebinarTracksContainerProps>`
  margin-left: ${({ keepTimeLineSpace }) => (keepTimeLineSpace ? 150 : 75)}px;
  margin-right: 75px;
  display: flex;
  flex-direction: row;
  width: calc(100% - ${({ keepTimeLineSpace }) => (keepTimeLineSpace ? 150 : 75)}px - 75px);

  &::after {
    content: "";
    display: block;
    width:
      ${
  ({ numWebinars, isOverflow }) => {
    if (!isOverflow) return 0;
    return (numWebinars === 1 ? 0 : 75);
  }
}px;
    flex: 0 0 auto;
  }

  @media (max-width: ${BREAKPOINT}px) {
    width: 100%;
    margin: 0;
    display: block;

    &::after {
      display: none;
    }
  }
`;

const WebinarWrapper = styled.div<{
  isMultiTrackSession: boolean;
  totalTracks: number;
  totalWebinars: number;
}>`
  min-width: ${({ isMultiTrackSession }) => (isMultiTrackSession ? '300px' : '100%')};
  overflow: hidden;
  flex:
    1 0 ${
  ({
    totalTracks,
    totalWebinars,
  }) => `${100 / Math.max(totalTracks, totalWebinars, 1)}%`
};

  @media (min-width: ${BREAKPOINT}px) {
    &:not(:last-child) {
      padding-right: 16px;
    }
  }
`;

interface TrackGroupProps {
  tracksGroup: LiveWebinarTracksGroup;
  options: LiveWebinarListOptions;
}

const GrouppedWebinarTracks = ({ tracksGroup, options }: TrackGroupProps) => {
  const locale = useSelector(selectLocale);
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINT}px)`);
  const groupId = useMemo(() => uuidv4(), []);
  const containerRef = useRef<HTMLDivElement>();
  const [isOverflow, setIsOverflow] = useState(false);
  const isMultiTracks = tracksGroup.tracks.length > 0;
  const multiTracksId = options?.multiTracks?.multiTracksId;
  const tracksTarget: any = tracksGroup.tracks.length > 1 && multiTracksId?.length > 0 ? multiTracksId : tracksGroup.tracks;
  const webinars = useMemo(() => tracksTarget
    .flatMap((track) => tracksGroup.trackIdWebinarMap[track?.id || track])
    .filter((webinar) => !!webinar)
    .sort(webinarSortFn(locale)), [tracksTarget, tracksGroup.trackIdWebinarMap, locale]);

  const detectOverflow = useCallback(() => {
    if (containerRef.current) {
      setIsOverflow(containerRef.current.scrollWidth > containerRef.current.clientWidth);
    }
  }, []);

  const equalizeHeight = useCallback(() => {
    const elements: Element[] = Array.from(document.getElementsByClassName(groupId));
    if (webinars.length > 1 && !isMobile) {
      setTimeout(() => {
        const heights = elements.map((element) => element.getBoundingClientRect().height);
        const maxHeight = Math.max(...heights);
        elements.forEach((element) => element.setAttribute('style', `height: ${maxHeight}px;`));
      }, 100);
    } else {
      elements.forEach((element) => element.setAttribute('style', 'height: unset;'));
    }
  }, [groupId, isMobile, webinars]);

  useEffect(() => {
    detectOverflow();
    window.addEventListener('resize', detectOverflow);
    return () => {
      window.removeEventListener('resize', detectOverflow);
    };
  }, []);

  useEffect(() => {
    equalizeHeight();
  }, [equalizeHeight]);

  return (
    <GroupedWebinarTracksContainer
      keepTimeLineSpace={options.timeLine.enable}
      numWebinars={webinars.length}
      isOverflow={isOverflow}
      ref={containerRef}
    >
      {
        webinars.map((webinar) => (
          <WebinarWrapper
            key={webinar.id}
            isMultiTrackSession={isMultiTracks}
            totalTracks={tracksGroup.tracks.length}
            totalWebinars={webinars.length}
          >
            {webinar && <LiveWebinarItem webinar={webinar} multiTrackOptions={options.multiTracks} groupId={groupId} />}
          </WebinarWrapper>
        ))
      }
    </GroupedWebinarTracksContainer>
  );
};

const LiveWebinarTracksGroupContainer = ({ tracksGroup, options }: TrackGroupProps) => (
  <RootContainer showTimeLine={options.timeLine.enable}>
    <MultiTracksHorizontalScrollbarHider maxHeight={Number.MAX_SAFE_INTEGER} options={options}>
      <GrouppedWebinarTracks tracksGroup={tracksGroup} options={options} />
    </MultiTracksHorizontalScrollbarHider>
  </RootContainer>
);

export default LiveWebinarTracksGroupContainer;
